import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { Policies } from '@app/auth/auth-policies';
import { AuthService } from '@app/auth/auth.service';
import { GiftCardDiscountType } from '@models/gift-cards/gift-card-discount-type';
import { GiftCardSettings } from '@models/gift-cards/gift-card-settings';
import { GiftCardsService } from '@services/gift-cards.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-gift-cards-settings',
  templateUrl: './gift-cards-settings.component.html',
  styleUrls: ['./gift-cards-settings.component.less'],
})
export class GiftCardsSettingsComponent implements OnInit, OnDestroy {
  private unsub: Subject<void> = new Subject<void>();
  private currentGiftCardSettings: GiftCardSettings;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  loading = false;
  readOnly = true;
  displayAddPreset = false;
  settingsFormGroup = new FormGroup({
    enableCustomAmounts: new FormControl(false),
    presetPurchaseAmounts: new FormControl([]),
    enableDiscount: new FormControl(false),
    discountType: new FormControl(null),
    discountValue: new FormControl(null),
    minLoad: new FormControl(null),
    maxDiscount: new FormControl(null),
    policy: new FormControl(''),
  });

  get presetAmounts(): number[] {
    return this.settingsFormGroup.get('presetPurchaseAmounts').value;
  }

  get GiftCardDiscountType() {
    return GiftCardDiscountType;
  }

  @ViewChild('addAmountInput') addAmountInput: ElementRef<HTMLInputElement>;

  constructor(private giftCardsService: GiftCardsService, private authService: AuthService) {}

  ngOnInit(): void {
    this.loading = true;
    this.readOnly = !this.authService.userSatisfiesPolicy(Policies.giftCardsEditSettings);

    if (this.readOnly) {
      this.settingsFormGroup.disable();
    }

    this.settingsFormGroup.get('enableDiscount').valueChanges.subscribe((enableDiscount) => {
      if (enableDiscount && !this.readOnly) {
        this.settingsFormGroup.get('discountType').enable();
        this.settingsFormGroup.get('discountValue').enable();
        this.settingsFormGroup.get('minLoad').enable();
        this.settingsFormGroup.get('maxDiscount').enable();
      } else {
        this.settingsFormGroup.get('discountType').disable();
        this.settingsFormGroup.get('discountValue').disable();
        this.settingsFormGroup.get('minLoad').disable();
        this.settingsFormGroup.get('maxDiscount').disable();
      }
    });

    this.giftCardsService.getGiftCardSettings().subscribe((settings) => {
      settings ??= {
        id: 0,
        enableCustomAmounts: false,
        presetPurchaseAmounts: [],
        enableDiscount: false,
        discountType: null,
        discountValue: null,
        minLoad: null,
        maxDiscount: null,
        policy: '',
      };
      this.currentGiftCardSettings = structuredClone(settings);
      this.settingsFormGroup.patchValue(settings);
      this.loading = false;
    });
  }

  addAmountClick() {
    this.displayAddPreset = true;
    setTimeout(() => this.addAmountInput.nativeElement.focus());
  }

  addPresetAmount(event: MatChipInputEvent) {
    const valueStr = (event.value || '').trim();
    if (valueStr) {
      const value = parseInt(valueStr, 10);
      const existingAmounts = this.presetAmounts;
      existingAmounts.push(value);
      existingAmounts.sort((a, b) => a - b);
      this.settingsFormGroup.get('presetPurchaseAmounts').setValue(existingAmounts);
      this.settingsFormGroup.get('presetPurchaseAmounts').markAsDirty();
    }
    event.chipInput.clear();
  }

  removePresetAmount(index: number) {
    const existingAmounts = this.presetAmounts;
    existingAmounts.splice(index, 1);
    this.settingsFormGroup.get('presetPurchaseAmounts').setValue(existingAmounts);
    this.settingsFormGroup.get('presetPurchaseAmounts').markAsDirty();
  }

  onSave() {
    const settings: GiftCardSettings = this.settingsFormGroup.value;
    settings.id = this.currentGiftCardSettings.id;
    this.loading = true;
    if (!settings.id) {
      this.giftCardsService.addGiftCardSettings(settings).subscribe((addedSettings) => {
        this.settingsFormGroup.patchValue(addedSettings);
        this.settingsFormGroup.markAsPristine();
        this.loading = false;
      });
    } else {
      this.giftCardsService.updateGiftCardSettings(settings).subscribe((updatedSettings) => {
        this.settingsFormGroup.patchValue(updatedSettings);
        this.settingsFormGroup.markAsPristine();
        this.loading = false;
      });
    }
    this.settingsFormGroup.pristine;
  }

  onCancel() {
    this.settingsFormGroup.patchValue(this.currentGiftCardSettings);
    this.settingsFormGroup.markAsPristine();
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
